// QuickChat — global mobile FAB + bottom-sheet for one-off voice
// questions to the assistant, accessible from any page (except chat
// itself, where the composer already has a mic).
//
// Flow:
//   tap FAB → fires MicListeningOverlay (existing component) → user
//   speaks → on final transcript, sends to the persistent "Quick" thread
//   via useQuickChat. The bottom sheet slides up showing the user's
//   message + streaming assistant reply. The mic button inside the
//   sheet is available for a follow-up turn in the same thread.
//
// Visibility:
//   - Mobile only (CSS hides on viewports >= 760px so desktop users
//     get the existing chat-tab affordance, which is always visible
//     in the sidebar there)
//   - Hidden when the user is currently on the chat tab (the
//     composer's mic covers that case)
//
// Replaces the previous TweaksPanel sparkle FAB on mobile — tweaks
// already lives in the Settings panel so we're not losing access.

import { useEffect, useRef, useState } from "preact/hooks";
import { Icon } from "../icons";
import { formatMd } from "../lib/format-md";
import { useMic } from "../lib/use-mic";
import { useQuickChat } from "../lib/use-quick-chat";
import { isKitchenAlarmActive, onKitchenAlarmActive, stopKitchenAlarm } from "../lib/kitchen-alarm";
import { MicListeningOverlay, playStartChime, playEndChime } from "./mic-listening-overlay";
import "./quick-chat.css";

// Keywords that silence the alarm during alarm-active listening. Matched
// against partial transcripts as substrings, case-insensitive — Parakeet
// usually emits these tokens within ~500 ms of utterance.
const STOP_ALARM_RE = /\b(stop|cancel|quiet|silence|enough|shut up)\b/i;
// [continue] and [end] are voice-flow markers — strip from display.
const STRIP_CONTINUE_RE = /\s*\[(?:continue|end)\]\s*$/i;

// End-of-conversation phrases. When the kiosk "Anything else?" auto-prompt
// re-opens the mic and the user says one of these, dismiss the bubble
// instead of round-tripping to the LLM (which would re-trigger another
// "Anything else?" and create an inescapable loop). Whole-utterance match
// only — "no I changed my mind" is a legitimate query, not a dismissal.
const END_CONVO_PHRASES = new Set([
  "no", "nope", "nothing", "nothing else",
  "thanks", "thank you", "no thanks", "no thank you",
  "im good", "im fine", "im all set", "no im good", "no im fine",
  "no thanks im good", "no thank you im good", "no im all set",
  "all done", "were done", "done",
  "end", "stop", "exit", "quit", "cancel", "dismiss",
  "bye", "goodbye", "bye bye",
  "thats all", "thats it", "thats all thanks", "thats it thanks",
  "nothing thanks", "nothing thank you", "nothing else thanks",
  "okay", "ok", "alright", "good",  // bare acknowledgement → done
]);
function isEndConversation(text: string): boolean {
  // Strip punctuation, apostrophes, smart quotes; collapse whitespace.
  let norm = text
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/[.!?,;:'"`]+/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!norm) return false;
  // Collapse consecutive duplicate words. STT often delivers
  // re-tries as repeats ("nope nope", "no no", "thanks thanks") when
  // the user said it twice waiting for visual confirmation — without
  // collapsing, these slip past the phrase set.
  norm = norm.split(" ").filter((w, i, a) => i === 0 || a[i - 1] !== w).join(" ");
  if (END_CONVO_PHRASES.has(norm)) return true;
  // Allow polite intro words: "no", "okay", "alright", "um", "uh", "well"
  // followed by an end phrase ("okay thanks", "well no I'm good").
  const stripped = norm.replace(/^(okay|ok|alright|um|uh|well|so)\s+/, "").trim();
  return END_CONVO_PHRASES.has(stripped);
}

interface QuickChatProps {
  /** Current active tab id from the app shell. We hide the FAB when
   *  tab === "chat" so the user doesn't see two mic buttons on the
   *  chat page (the composer already has one). */
  currentTab: string;
  /** Active kiosk profile if any. When set (e.g. "kitchen") the bubble
   *  switches to a hands-free, full-screen layout and auto-relistens
   *  after the assistant's reply finishes speaking. */
  kioskProfile?: string | null;
}

export function QuickChat({ currentTab, kioskProfile }: QuickChatProps) {
  const isKiosk = kioskProfile === "kitchen";
  const qc = useQuickChat();
  // Bubble stays open as long as there's at least one turn. The dismiss
  // X clears the local turns and closes; the FAB starts a fresh
  // conversation each time (clearing any prior turns).
  const [sheetOpen, setSheetOpen] = useState(false);
  // Live partial transcript from the STT stream. Shown in the bubble
  // while mic is active so the user sees their words land in real
  // time — eliminates the "did it hear me?" anxiety while the VAD
  // silence timeout is still draining. Cleared on stream-final
  // (replaced by the committed user turn) and on mic-cancel/dismiss.
  const [partialText, setPartialText] = useState("");

  const mic = useMic({
    // While an alarm is ringing the mic is auto-opened (see kitchen-alarm
    // hook below). Watch partials for a stop keyword and silence the
    // alarm + cancel the mic before any of this audio routes to chat.
    onStreamPartial: (text) => {
      setPartialText(text);
      if (isKitchenAlarmActive() && STOP_ALARM_RE.test(text)) {
        stopKitchenAlarm();
        mic.cancel();
      }
    },
    onStreamFinal: (text) => {
      // Partial is replaced by the committed turn from here on.
      setPartialText("");
      const trimmed = text.trim();
      if (!trimmed) return;
      // Even on the final transcript, swallow stop-during-alarm so we
      // don't accidentally send "stop the timer" to the chat agent.
      if (isKitchenAlarmActive() && STOP_ALARM_RE.test(trimmed)) {
        stopKitchenAlarm();
        return;
      }
      // Kiosk auto-relisten loop escape: when the user replies to the
      // "Anything else?" prompt with a dismissal ("no thanks", "I'm
      // good", "bye"), close the bubble instead of round-tripping
      // through the LLM — otherwise every reply gets another
      // "Anything else?" and the user can't disengage by voice.
      if (isKiosk && isEndConversation(trimmed)) {
        stopTts();
        setSheetOpen(false);
        qc.reset();
        return;
      }
      setSheetOpen(true);
      void qc.sendMessage(trimmed);
    },
    onError: () => { /* surfaced via the mic overlay's own path */ },
  });

  // Kitchen-alarm hooks: (1) when an alarm starts ringing, auto-open the
  // mic so the user can say "stop" without "Hey Vulture" first.
  // (2) hooked separately in the wake handler below — wake also stops
  // the alarm so saying the wake word silences it.
  useEffect(() => {
    return onKitchenAlarmActive((active) => {
      if (!active) return;
      if (!mic.available) return;
      if (mic.state !== "idle") return;
      void mic.toggle();
    });
  }, [mic]);

  // Kitchen kiosk wake-word integration. When the Electron shell
  // publishes window.VultureKitchen.onWake (after openWakeWord fires
  // on the Surface mic), kick off the same flow as a FAB tap — drop
  // any prior turns, open the bubble, start mic. Refs keep the deps
  // empty so we subscribe once instead of churning on every render.
  const micRef = useRef(mic);
  const qcRef = useRef(qc);
  useEffect(() => { micRef.current = mic; qcRef.current = qc; });
  useEffect(() => {
    const bridge = (window as unknown as {
      VultureKitchen?: { onWake?: (h: () => void) => () => void };
    }).VultureKitchen;
    if (!bridge?.onWake) return;
    return bridge.onWake(() => {
      // Saying the wake word while an alarm is ringing also silences it
      // — treats wake as implicit acknowledgement so the follow-up
      // utterance isn't competing with the beep.
      if (isKitchenAlarmActive()) stopKitchenAlarm();
      const m = micRef.current;
      if (!m.available || m.state !== "idle") return;
      qcRef.current.reset();
      setSheetOpen(true);
      void m.toggle();
    });
  }, []);

  // Chime on every mic open/close — not just the first one. The mic
  // overlay used to own this, but it doesn't remount for the
  // auto-follow-up reopen (the sheet renders instead), leaving the
  // user uncertain whether the mic was actually listening. Firing on
  // state transitions covers all three entry points: FAB tap, wake
  // word, and follow-up auto-open.
  const prevMicStateRef = useRef(mic.state);
  useEffect(() => {
    const prev = prevMicStateRef.current;
    prevMicStateRef.current = mic.state;
    if (mic.state === "streaming" && prev !== "streaming") void playStartChime();
    if (mic.state === "transcribing" && prev === "streaming") void playEndChime();
    // Clear the partial caption whenever we leave the active capture
    // states (covers cancel, errors, follow-up restart). onStreamFinal
    // also clears it on the happy path; this is the defensive sweep.
    if (mic.state === "idle" && prev !== "idle") setPartialText("");
  }, [mic.state]);

  // Sentence-streaming TTS: instead of waiting for the full assistant
  // reply to finish streaming and then synthesizing one big audio,
  // we detect sentence boundaries on the fly. As each complete
  // sentence arrives we POST it to /api/tts in parallel and queue
  // the returned audio. Sequential playback so the user hears
  // sentence 1 while Piper is already synthesizing sentence 2.
  //
  // Time-to-first-audio drops from "full LLM reply + full TTS synth"
  // to "first sentence of LLM + ~0.3 s Piper synth" — about 1 s in
  // practice for a 3-sentence reply that previously took 4-5 s.
  //
  // Fallback: any /api/tts failure for a given sentence triggers
  // window.VultureTts.speak() for that sentence, so offline mobile
  // still gets a voice. The native TTS isn't queued — it just fires.

  // Pending sentences waiting to play (audio elements ready) +
  // currently-playing audio. New sentences get appended; playback
  // chain runs through the queue sequentially.
  type QueuedAudio = { url: string; audio: HTMLAudioElement };
  const ttsQueueRef = useRef<QueuedAudio[]>([]);
  const ttsCurrentRef = useRef<HTMLAudioElement | null>(null);
  // Number of chars of the trailing turn's assistant text already
  // consumed by the sentence extractor. Lets us only synthesize the
  // NEW text on each render rather than rescanning from the start.
  const ttsConsumedRef = useRef(0);
  // Tracks which turn the consumed counter belongs to. When the user
  // sends a follow-up, the trailing turn index advances and we reset.
  const ttsTurnIndexRef = useRef(-1);
  // AbortController for in-flight /api/tts fetches — abort()ed on
  // dismiss / follow-up so we don't queue late-arriving audio.
  const ttsAbortRef = useRef<AbortController | null>(null);

  // Track whether we just finished playing back a turn — so we know to
  // auto-relisten when the TTS queue drains. Set by the qc.turns effect
  // when it enqueues; cleared when we relisten.
  const turnReadyForFollowupRef = useRef(false);
  // Set when the model's reply ended with [end] — the symmetric close
  // to [continue]. When the TTS queue drains, dismiss the bubble
  // instead of either auto-relistening or appending "Anything else?".
  // Cleared on dismiss / new wake / new turn.
  const endRequestedRef = useRef(false);

  // Reactive mirror of "TTS is currently playing or has queued audio".
  // Drives the auto-dismiss gate in QuickChatSheet so the 20s kiosk
  // timeout doesn't yank the bubble closed mid-response. Refs alone
  // can't trigger the child effect's re-run; state can.
  const [ttsActive, setTtsActive] = useState(false);

  const playNextInQueue = () => {
    const queue = ttsQueueRef.current;
    if (queue.length === 0) {
      ttsCurrentRef.current = null;
      setTtsActive(false);
      // [end] marker honored: the polite close-line finished playing,
      // dismiss the bubble. Wins over auto-relisten so the kiosk
      // doesn't immediately re-open the mic right after "Goodbye".
      if (endRequestedRef.current) {
        endRequestedRef.current = false;
        turnReadyForFollowupRef.current = false;
        if (isKiosk) {
          setSheetOpen(false);
          qc.reset();
        }
        return;
      }
      // Kiosk auto-listen: after the assistant's reply finishes
      // speaking, re-open the mic so the user can ask a follow-up
      // without saying "Hey Vulture" again. Gated on isKiosk so the
      // regular SPA flow doesn't surprise mobile/desktop users.
      // Auto-relisten fires anywhere with a mic bridge (kiosk + Android),
      // gated on the model having emitted [continue] in its last reply.
      if (micRef.current.available && turnReadyForFollowupRef.current && micRef.current.state === "idle") {
        turnReadyForFollowupRef.current = false;
        // setTimeout 0 to escape the audio-element onended callback
        // before kicking off a new WS + mic open.
        setTimeout(() => { void micRef.current.toggle(); }, 0);
      }
      return;
    }
    const next = queue.shift()!;
    ttsCurrentRef.current = next.audio;
    next.audio.onended = () => {
      try { URL.revokeObjectURL(next.url); } catch { /* ignore */ }
      if (ttsCurrentRef.current === next.audio) ttsCurrentRef.current = null;
      // Small gap (~150 ms) between chunks. The Piper WAVs have minimal
      // trailing silence; without a pause the listener perceives the
      // last word of chunk N getting swallowed by the start of chunk
      // N+1. 150 ms also reads as a natural sentence break.
      setTimeout(playNextInQueue, 150);
    };
    // Wait for the audio to be fully buffered before play(). Without
    // this, fast TTS (short clips) gets its first ~100ms clipped
    // because play() fires before the decoder has any samples ready.
    // Force currentTime to 0 in case the element nudged forward during
    // load (observed when length_scale is aggressive and clips are < 1s).
    //
    // Idempotency guard: startPlayback can be called from up to TWO
    // sources (canplaythrough listener + 500ms fallback timer) for the
    // same audio element. play() returns a Promise that resolves only
    // when playback actually begins — between play() and that resolve,
    // audio.paused is still true. If the fallback timer's "paused?"
    // check fires during that window, it would call play() AGAIN,
    // which resumes from the in-progress buffer position instead of
    // 0 — perceived as the audio "jumping mid-sentence." The flag
    // makes startPlayback fire-once per element.
    let started = false;
    const startPlayback = () => {
      if (started) return;
      started = true;
      try { next.audio.currentTime = 0; } catch { /* ignore */ }
      next.audio.play().catch(() => {
        try { URL.revokeObjectURL(next.url); } catch { /* ignore */ }
        playNextInQueue();
      });
    };
    if (next.audio.readyState >= 4) {
      startPlayback();
    } else {
      next.audio.addEventListener("canplaythrough", startPlayback, { once: true });
      // Belt-and-suspenders: if canplaythrough never fires, fall back
      // after 500ms so the queue can't deadlock on a stuck element.
      setTimeout(() => {
        if (next.audio.paused && ttsCurrentRef.current === next.audio) {
          startPlayback();
        }
      }, 500);
    }
  };

  const enqueueSentence = async (sentence: string, ac: AbortController) => {
    try {
      // length_scale 0.5 ≈ 2× the default Piper rate. Kitchen kiosk
      // listeners are usually mid-task (hands wet, kid asking the next
      // thing) — the default rate felt unnecessarily slow. Mobile FAB
      // users keep the natural rate.
      const body = isKiosk
        ? { text: sentence, length_scale: 0.5 }
        : { text: sentence };
      const r = await fetch("/api/tts", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Mirror-RPC": "1" },
        body: JSON.stringify(body),
        signal: ac.signal,
      });
      if (!r.ok) throw new Error(`tts ${r.status}`);
      const blob = await r.blob();
      if (ac.signal.aborted) return;
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      ttsQueueRef.current.push({ url, audio });
      setTtsActive(true);
      if (!ttsCurrentRef.current) {
        playNextInQueue();
      }
    } catch (err) {
      if (ac.signal.aborted) return;
      // Fall back to native TTS for THIS sentence; let the next
      // sentences keep using Piper.
      try { window.VultureTts?.speak(sentence); } catch { /* ignore */ }
    }
  };

  const stopTts = () => {
    ttsAbortRef.current?.abort();
    ttsAbortRef.current = null;
    if (ttsCurrentRef.current) {
      try { ttsCurrentRef.current.pause(); } catch { /* ignore */ }
      ttsCurrentRef.current = null;
    }
    for (const { url, audio } of ttsQueueRef.current) {
      try { audio.pause(); } catch { /* ignore */ }
      try { URL.revokeObjectURL(url); } catch { /* ignore */ }
    }
    ttsQueueRef.current = [];
    setTtsActive(false);
    try { window.VultureTts?.stop(); } catch { /* ignore */ }
  };

  // Extract complete "speakable chunks" from `text`. Strong boundaries
  // (sentence-end punctuation: . ! ?) flush immediately. Weak boundaries
  // (clause-end: , ; :) only flush once enough chars have accumulated
  // since the last flush (MIN_CLAUSE_CHARS) — keeps the first audio
  // chunk reasonably substantial while still firing well before the
  // full sentence completes. Returns the chunks + the number of chars
  // consumed; the trailing partial stays in the buffer for next pass.
  const MIN_CLAUSE_CHARS = 15;
  const extractSentences = (text: string): { sentences: string[]; consumed: number } => {
    const sentences: string[] = [];
    let i = 0;
    let lastEnd = 0;
    while (i < text.length) {
      const ch = text[i];
      const isSentenceEnd = ch === "." || ch === "!" || ch === "?";
      const isClauseEnd = ch === "," || ch === ";" || ch === ":";
      if (isSentenceEnd || isClauseEnd) {
        let j = i + 1;
        // Allow stacked terminators ("?!" "..." etc.) + closing quotes/brackets.
        while (j < text.length && /[.!?"')\]]/.test(text[j])) j++;
        // Boundary fires only if followed by whitespace (mid-buffer);
        // end-of-buffer is handled by the caller's isFinal flush path.
        if (j < text.length && /\s/.test(text[j])) {
          const chunkLen = j - lastEnd;
          // Sentence-end always fires; clause-end only fires when the
          // chunk has enough material to sound natural on its own.
          if (isSentenceEnd || chunkLen >= MIN_CLAUSE_CHARS) {
            const sentence = text.slice(lastEnd, j).trim();
            if (sentence) sentences.push(sentence);
            while (j < text.length && /\s/.test(text[j])) j++;
            lastEnd = j;
            i = j;
            continue;
          }
        }
      }
      i++;
    }
    return { sentences, consumed: lastEnd };
  };

  useEffect(() => {
    const lastIndex = qc.turns.length - 1;
    const last = qc.turns[lastIndex];
    if (!last) return;

    // New trailing turn (user sent a new message) — reset queue/state.
    if (lastIndex !== ttsTurnIndexRef.current) {
      stopTts();
      ttsConsumedRef.current = 0;
      ttsTurnIndexRef.current = lastIndex;
      ttsAbortRef.current = new AbortController();
    }
    // Ensure we always have an active abort controller for this turn.
    if (!ttsAbortRef.current) ttsAbortRef.current = new AbortController();
    const ac = ttsAbortRef.current;

    // The LLM is fast enough (no-think on top of a hot model) that the
    // marginal latency of waiting for the full reply is negligible —
    // and one-shot synthesis avoids the per-chunk audio-startup race
    // that was clipping the first ~100ms of fast clips. Synthesize
    // only when the turn finishes streaming.
    if (last.pending) return;
    const rawText = last.assistant.slice(ttsConsumedRef.current).trim();
    if (!rawText) return;
    ttsConsumedRef.current = last.assistant.length;
    // The model emits `[continue]` at the end of replies where it
    // expects a follow-up answer (asked a question or sought
    // clarification), or `[end]` when the user's last utterance was
    // a dismissal and the model is acknowledging it ("you're welcome",
    // "goodbye"). [end] is the symmetric close — speak the polite
    // close-line, then dismiss the kiosk bubble; do NOT auto-relisten.
    // Punctuation fallback for [continue]: if the reply ends in a
    // literal question mark we also auto-relisten — covers cases
    // where the model asks something without remembering the marker.
    const endMatch = /\s*\[end\]\s*$/i.exec(rawText);
    const continueMatch = !endMatch ? /\s*\[continue\]\s*$/i.exec(rawText) : null;
    const stripIndex = endMatch ? endMatch.index : (continueMatch ? continueMatch.index : rawText.length);
    const baseText = (endMatch || continueMatch) ? rawText.slice(0, stripIndex).trim() : rawText;
    const endsWithQuestion = /\?["')\]]*\s*$/.test(baseText);
    // Kiosk auto-followup prompt: if the reply ends as a STATEMENT
    // (not a question, no [continue] marker, NOT an [end] close), tack
    // on "Anything else?" so the user gets an audible signal that the
    // turn is done AND the mic auto-relistens. [end] short-circuits
    // this — the polite close-line goes through TTS verbatim and
    // turnReady stays false so no auto-relisten fires.
    const appendAnythingElse = isKiosk && !continueMatch && !endMatch && !endsWithQuestion;
    const fullText = appendAnythingElse ? `${baseText} Anything else?` : baseText;
    turnReadyForFollowupRef.current = !endMatch && (!!continueMatch || endsWithQuestion || appendAnythingElse);
    // [end] marker: after the close-line finishes speaking, dismiss
    // the bubble. Schedule on the TTS-drain side so the user actually
    // HEARS "you're welcome" before the sheet closes — using a flag
    // the playNextInQueue drain path picks up.
    if (endMatch) {
      endRequestedRef.current = true;
    }
    if (!fullText) return;
    void enqueueSentence(fullText, ac);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc.turns]);

  const handleFabTap = () => {
    if (!mic.available) {
      // Browser without the native bridge — silently no-op. The FAB
      // shouldn't have rendered in this case (CSS hides it on
      // desktop and the bridge is mobile-only) but defensive.
      return;
    }
    // Fresh conversation each FAB tap — drop any stale turns so the
    // bubble pops with just the new question + response.
    stopTts();
    ttsConsumedRef.current = 0;
    ttsTurnIndexRef.current = -1;
    qc.reset();
    void mic.toggle();
    // Pre-warm /api/tts in the background so the first real synth
    // request after the user finishes speaking doesn't pay cold-start
    // costs: CF-tunnel TLS handshake, mirror-agent proxy connection,
    // and Piper's first-inference onnxruntime JIT. We fire a tiny
    // discardable synth and toss the response. Best-effort — failure
    // is silent; the real flow handles its own errors.
    void fetch("/api/tts", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Mirror-RPC": "1" },
      body: JSON.stringify({ text: "." }),
    }).then((r) => r.blob()).catch(() => { /* swallow */ });
  };

  const handleSheetDismiss = () => {
    stopTts();
    ttsConsumedRef.current = 0;
    ttsTurnIndexRef.current = -1;
    setSheetOpen(false);
    qc.reset();
  };

  const handleSheetMicTap = () => {
    // Follow-up — cancel any in-flight TTS so the previous answer
    // doesn't keep talking over the user's new question.
    stopTts();
    void mic.toggle();
  };

  // FAB visibility gating: mobile only (CSS handles viewport) AND not
  // on the chat tab. The component still mounts on chat — only the
  // FAB element is hidden — so an in-flight quick-chat sheet stays
  // visible if the user navigates to chat mid-stream.
  const fabHidden = currentTab === "chat";

  return (
    <>
      {!fabHidden && mic.available && mic.state === "idle" && !sheetOpen && (
        <button
          className="qc-fab"
          title="Ask the assistant"
          onClick={handleFabTap}
          type="button"
        >
          {/* Vulture silhouette standing on a chat bubble. Rendered via
            * CSS mask-image so the black-on-transparent PNG is recolored
            * to whatever --accent-ink resolves to (follows per-user
            * theme automatically). The bubble is part of the artwork
            * so no separate chat-icon overlay needed. */}
          <span className="qc-fab-vulture" aria-hidden="true" />
        </button>
      )}

      {/* Mobile FAB flow: overlay covers initial dictation; bubble
        * shows once a turn lands. Kiosk skips the overlay entirely —
        * partials render directly in the bubble so the user sees
        * their voice landing from the moment the mic opens. */}
      {!isKiosk && (mic.state === "streaming" || mic.state === "transcribing") && !(sheetOpen && qc.turns.length > 0) && (
        <MicListeningOverlay
          state={mic.state}
          level={mic.level}
          onStop={() => void mic.toggle()}
          onCancel={() => mic.cancel()}
        />
      )}

      {/* Kiosk shows the bubble whenever the sheet is open — even with
        * no turns yet, so the partial transcript has somewhere to
        * land during the FIRST dictation after wake. Mobile keeps the
        * old "turns required" gate since the overlay covers the
        * pre-first-turn period. */}
      {sheetOpen && (qc.turns.length > 0 || (isKiosk && (mic.state !== "idle" || partialText))) && (
        <QuickChatSheet
          turns={qc.turns}
          micState={mic.state}
          micAvailable={mic.available}
          onMicTap={handleSheetMicTap}
          onDismiss={handleSheetDismiss}
          kiosk={isKiosk}
          ttsActive={ttsActive}
          partialText={partialText}
        />
      )}
    </>
  );
}

interface QuickChatTurnView {
  user: string;
  assistant: string;
  toolCalls: { id: string; name: string; args: unknown; result: string | null; denied?: boolean; error?: string }[];
  pending: boolean;
}

interface QuickChatSheetProps {
  turns: QuickChatTurnView[];
  micState: "idle" | "streaming" | "transcribing" | "uploading";
  micAvailable: boolean;
  onMicTap: () => void;
  onDismiss: () => void;
  kiosk?: boolean;
  ttsActive?: boolean;
  /** Live STT partial transcript while the mic is streaming. Shown
   *  below the latest turn so the user can see their words landing
   *  in real time — fixes the "did it hear me?" anxiety during the
   *  VAD-silence wait. Cleared on stream-final / cancel. */
  partialText?: string;
}

function QuickChatSheet({ turns, micState, micAvailable, onMicTap, onDismiss, kiosk, ttsActive, partialText }: QuickChatSheetProps) {
  // Auto-scroll the body to the latest turn as content arrives.
  const bodyRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  // Kiosk auto-dismiss: 20 s after the latest activity. Skip while the
  // mic is active, while the LLM is still streaming, AND while TTS is
  // still speaking the reply — without the TTS gate, a long response
  // (LLM finished early, TTS still mid-playback) gets the bubble
  // dismissed AND its audio cut off at 20 s flat.
  useEffect(() => {
    if (!kiosk) return;
    if (micState !== "idle") return;
    if (ttsActive) return;
    const lastTurnPending = turns.length > 0 && turns[turns.length - 1].pending;
    if (lastTurnPending) return;
    const timer = window.setTimeout(onDismiss, 20_000);
    return () => window.clearTimeout(timer);
  }, [kiosk, micState, turns, onDismiss, ttsActive]);

  const lastTurnPending = turns.length > 0 && turns[turns.length - 1].pending;

  return (
    <>
      {/* Lightweight catch-all click target for outside-tap-to-dismiss.
        * No dim/backdrop — the page behind stays fully visible because
        * the bubble itself is the focus, not a modal. */}
      <div className="qc-bubble-closezone" onClick={onDismiss} aria-hidden="true" />
      <div className="qc-bubble" data-kiosk={kiosk ? "kitchen" : undefined} data-mic={micState} role="dialog" aria-label="Quick chat">
        <button
          className="qc-bubble-x"
          title="Dismiss"
          onClick={onDismiss}
          type="button"
        >
          <Icon name="x" size={14} />
        </button>

        <div className="qc-bubble-body" ref={bodyRef}>
          {/* Show ONLY the latest turn in the bubble. Prior turns stay
            * in qc.turns so the conversation array sent to the server
            * still preserves context — they just don't crowd the small
            * popup view. Stacked turns made the kiosk window unreadable
            * after 2-3 exchanges. */}
          {turns.slice(-1).map((t, i) => (
            <QuickChatTurnRow key={turns.length - 1 + i} turn={t} />
          ))}
          {/* Live STT partial — shown while the mic is capturing so
            * the user gets immediate visual feedback that their voice
            * is landing. Renders as a dimmed user-side bubble below
            * any committed turn. Cleared by onStreamFinal (replaced
            * by the real committed turn). When the mic is active but
            * no partial has arrived yet (just opened), show a
            * "Listening…" hint in the same slot so the bubble doesn't
            * look empty. Kiosk-flow only — mobile uses the overlay. */}
          {(micState === "streaming" || micState === "transcribing") && (
            <div className="qc-imsg qc-imsg-user qc-imsg-partial">
              <div className="qc-imsg-bubble">
                {partialText || (micState === "transcribing" ? "Transcribing…" : "Listening…")}
              </div>
            </div>
          )}
        </div>

        {micAvailable && (
          <div className="qc-bubble-footer">
            <button
              className="qc-followup-mic"
              data-mic={micState}
              title="Follow up"
              onClick={onMicTap}
              disabled={micState !== "idle" || lastTurnPending}
              type="button"
            >
              <Icon name="mic" size={16} />
              <span>Ask follow-up</span>
            </button>
          </div>
        )}

        {/* Tail/notch pointing down-right toward the FAB. Pure CSS — see qc-bubble::after. */}
      </div>
    </>
  );
}

function QuickChatTurnRow({ turn }: { turn: QuickChatTurnView }) {
  // Tool calls intentionally hidden in QC. The voice flow's job is to
  // deliver the OUTCOME of the model's work — the user doesn't need to
  // see "Used shopping_add_items" chips for a "milk added" reply, and
  // the chips made the bubble noisier without adding value. The model
  // is also instructed to never narrate tool calls in the spoken reply
  // (see use-quick-chat.ts systemPromptAppend), so the assistant text
  // should already read as a clean outcome statement.
  return (
    <>
      <div className="qc-imsg qc-imsg-user">
        <div className="qc-imsg-bubble">{turn.user}</div>
      </div>

      <div className="qc-imsg qc-imsg-assistant">
        <div className="qc-imsg-bubble">
          {turn.assistant ? (
            <span dangerouslySetInnerHTML={{ __html: formatMd(turn.assistant.replace(STRIP_CONTINUE_RE, "")) }} />
          ) : turn.pending ? (
            <span className="qc-thinking">…</span>
          ) : ""}
          {turn.pending && turn.assistant && <span className="qc-caret">▌</span>}
        </div>
      </div>
    </>
  );
}
